<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import {
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import ContactPanelHistory from './ContactPanelHistory.vue';
import SzProductsPanel from 'dashboard/components/widgets/conversation/solarz/SzProductsPanel.vue';
import SzScheduledMessages from 'dashboard/components/widgets/conversation/solarz/SzScheduledMessages.vue';
// Group participants — handled exclusively by /sz-group-admin.js (override)
// which injects the panel inline before the "Informações da conversa" h4.
// Esse override é mais completo (botão Gerenciar abre modal full add/remove/
// promote, design alinhado com o resto do projeto), enquanto a versão Vue
// tinha só listagem básica.

const TAB_STORAGE_KEY = 'sz_contact_panel_tab';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const { updateUISettings } = useUISettings();

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);

const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

// Tabs
const initialTab = (() => {
  try {
    const stored = localStorage.getItem(TAB_STORAGE_KEY);
    return stored === 'history' ? 'history' : 'details';
  } catch (e) { return 'details'; }
})();
const activeTab = ref(initialTab);

const setTab = tab => {
  if (tab === activeTab.value) return;
  activeTab.value = tab;
  try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch (e) {}
  if (tab === 'history' && contactId.value) {
    store.dispatch('contactConversations/get', contactId.value);
  }
};

// History data — fetched eagerly so the tab counter is accurate from the start
const contactConversationsGetter = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const historyCount = computed(() => {
  const list = contactConversationsGetter.value(contactId.value) || [];
  return list.length;
});

// Tab bar só aparece se houver MAIS de 1 chat — caso contrário o único chat
// na lista é a própria conversa atual e a tab "Histórico" ficaria vazia.
const hasMultipleChats = computed(() => historyCount.value > 1);

// Se a tab salva era "history" mas o contato só tem 1 chat, volta pra
// "details" automaticamente.
watch(hasMultipleChats, multi => {
  if (!multi && activeTab.value === 'history') {
    activeTab.value = 'details';
    try { localStorage.setItem(TAB_STORAGE_KEY, 'details'); } catch (e) {}
  }
});

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
    store.dispatch('contactConversations/get', contactId.value);
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

onMounted(() => {
  getContactDetails();
});
</script>

<template>
  <div class="w-full">
    <!-- Custom header com botão X garantido (não depende de plugin de ícones) -->
    <div class="sz-contact-header flex items-center justify-between px-4 py-2 border-b border-n-weak h-14">
      <span class="font-medium text-sm text-n-slate-12">{{ $t('CONVERSATION.SIDEBAR.CONTACT') }}</span>
      <button
        type="button"
        :title="$t('GENERAL.CLOSE')"
        class="flex items-center justify-center w-9 h-9 rounded-md text-n-slate-12 hover:bg-n-alpha-2 transition-colors -mr-1"
        @click="closeContactPanel"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
    <ContactInfo :contact="contact" :channel-type="channelType" header-only />

    <!--
      SZ: Tab switcher com indicador deslizante (sliding pill).
      Um <span class="sz-tab-pill"> posicionado em absolute desliza entre as
      duas tabs via transform, em vez do highlight branco aparecer/sumir.
      Os botões viram apenas labels — sem background condicional.
    -->
    <div v-if="hasMultipleChats" class="px-4 pt-3 pb-2 border-t border-n-weak">
      <div
        class="relative flex p-1 rounded-lg bg-n-alpha-1 sz-tab-wrap"
        :class="`sz-tab-wrap--${activeTab}`"
      >
        <span class="sz-tab-pill bg-n-solid-1 shadow-sm" aria-hidden="true"></span>
        <button
          type="button"
          class="relative z-10 flex-1 px-3 py-1.5 text-sm font-medium transition-colors"
          :class="activeTab === 'details' ? 'text-n-slate-12' : 'text-n-slate-11 hover:text-n-slate-12'"
          @click="setTab('details')"
        >Detalhes</button>
        <button
          type="button"
          class="relative z-10 flex-1 px-3 py-1.5 text-sm font-medium transition-colors"
          :class="activeTab === 'history' ? 'text-n-slate-12' : 'text-n-slate-11 hover:text-n-slate-12'"
          @click="setTab('history')"
        >Histórico<span v-if="historyCount" class="ml-1 opacity-70">({{ historyCount }})</span></button>
      </div>
    </div>

    <!-- Detalhes do contato (sempre renderizado quando não há multi-chat;
         ou condicional pela tab quando há). -->
    <div v-show="!hasMultipleChats || activeTab === 'details'">
      <ContactInfo :contact="contact" :channel-type="channelType" body-only :show-avatar="false" />
    </div>
    <div v-show="hasMultipleChats && activeTab === 'history'">
      <ContactPanelHistory
        v-if="contactId"
        :contact-id="contactId"
        :current-conversation-id="conversationId"
      />
    </div>

    <!-- Demais seções — sempre visíveis -->
    <ConversationAction :conversation-id="conversationId" :inbox-id="inboxId" />
    <ConversationParticipant :conversation-id="conversationId" :inbox-id="inboxId" />

    <!-- Mensagens agendadas -->
    <SzScheduledMessages :conversation-id="conversationId" />

    <!-- Informações da conversa -->
    <div class="border-t border-n-weak">
      <h4 class="text-sm font-medium text-n-slate-12 m-0 px-4 pt-3 pb-1">
        Informações da conversa
      </h4>
      <div class="px-4 pb-3">
        <ConversationInfo
          :conversation-attributes="conversationAdditionalAttributes"
          :contact-attributes="contactAdditionalAttributes"
        />
      </div>
    </div>

    <!-- Notas do contato (título + botão Adicionar estão dentro do componente) -->
    <div class="border-t border-n-weak pb-8">
      <ContactNotes v-if="contactId" :contact-id="contactId" />
    </div>

    <!-- Produtos SolarZ -->
    <SzProductsPanel />
  </div>
</template>

<style scoped lang="scss">
// SZ: indicador deslizante (sliding pill) entre as duas tabs.
// Um span absoluto cobre exatamente metade do container (descontando padding)
// e se move via translateX. O `cubic-bezier` é o easeOutQuart do iOS — sensação
// natural de deslizar e parar, sem rebote.
.sz-tab-wrap {
  // necessário pra position:absolute do pill ancorar aqui
  isolation: isolate;
}

.sz-tab-pill {
  position: absolute;
  top: 0.25rem;     // = p-1 do container
  bottom: 0.25rem;
  left: 0.25rem;
  width: calc(50% - 0.25rem);
  border-radius: 0.375rem;  // = rounded-md
  transition: transform 240ms cubic-bezier(0.32, 0.72, 0, 1);
  pointer-events: none;
  z-index: 0;
}

// Quando "history" está ativo, desliza pra cobrir a segunda tab
.sz-tab-wrap--history .sz-tab-pill {
  transform: translateX(100%);
}
</style>
